import type { ColorPalette } from '../constants/colors';
import type { AttendanceHistoryItem } from '../types/attendance';
import type { HolidayRecord } from '../types/officeCalendar';
import { formatTime } from './dateTime';
import { holidaysByDate } from './attendanceHolidays';

export type AttendanceCalendarTone =
  | 'present'
  | 'absent'
  | 'leave'
  | 'wfh'
  | 'holiday'
  | 'permission'
  | 'neutral';

export type AttendanceCalendarDay = {
  date: string;
  day: number;
  inMonth: boolean;
  item: AttendanceHistoryItem | null;
  tone: AttendanceCalendarTone;
  holidayName: string | null;
};

const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;

export function attendanceWeekdayLabels() {
  return WEEKDAY_LABELS;
}

export function formatAttendanceMonthTitle(year: number, monthIndex: number): string {
  const date = new Date(year, monthIndex, 1);
  return new Intl.DateTimeFormat(undefined, {
    month: 'long',
    year: 'numeric',
  }).format(date);
}

/** Mock-style date: "24 May 2024" */
export function formatAttendanceListDate(value: string | null | undefined): string {
  if (!value) return 'Date unavailable';
  const dateOnly = value.trim().slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateOnly)) return 'Date unavailable';
  const date = new Date(`${dateOnly}T12:00:00`);
  if (Number.isNaN(date.getTime())) return 'Date unavailable';
  return new Intl.DateTimeFormat(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(date);
}

export function formatAttendanceClock(
  localTimestamp?: string | null,
  fallback?: string | null,
): string {
  const raw = localTimestamp?.trim() || fallback?.trim();
  if (!raw) return '--';
  const formatted = formatTime(raw);
  return formatted === 'Not available' ? '--' : formatted;
}

export type MonthOverviewCounts = {
  present: number;
  absent: number;
  leave: number;
  wfh: number;
};

function monthOverviewTone(
  item: Pick<AttendanceHistoryItem, 'statusCode' | 'statusLabel'>,
): keyof MonthOverviewCounts | null {
  const tone = resolveAttendanceCalendarTone(item);
  if (
    tone === 'present' ||
    tone === 'absent' ||
    tone === 'leave' ||
    tone === 'wfh'
  ) {
    return tone;
  }
  const code = (item.statusCode ?? '').trim().toUpperCase();
  const label = item.statusLabel.trim().toLowerCase();
  if (code === 'LATE' || code === 'LATE_ARRIVAL' || label.includes('late')) {
    return 'present';
  }
  return null;
}

/** Unique calendar days in the current month, counted by attendance tone. */
export function countMonthOverview(
  items: Array<Pick<AttendanceHistoryItem, 'attendanceDate' | 'statusCode' | 'statusLabel'>>,
): MonthOverviewCounts {
  const byDate = new Map<string, keyof MonthOverviewCounts>();
  for (const item of items) {
    const date = item.attendanceDate.slice(0, 10);
    if (!date || byDate.has(date)) continue;
    const tone = monthOverviewTone(item);
    if (tone) byDate.set(date, tone);
  }
  const counts: MonthOverviewCounts = {
    present: 0,
    absent: 0,
    leave: 0,
    wfh: 0,
  };
  for (const tone of byDate.values()) {
    counts[tone] += 1;
  }
  return counts;
}

export function resolveAttendanceCalendarTone(
  item: Pick<AttendanceHistoryItem, 'statusCode' | 'statusLabel'> | null,
): AttendanceCalendarTone {
  if (!item) return 'neutral';
  const code = (item.statusCode ?? '').trim().toUpperCase();
  const label = item.statusLabel.trim().toLowerCase();

  // Day status codes decide before label wording. A quarter-day status can
  // name an absent share and still be a leave day.
  if (code === 'HALF_DAY') return 'absent';
  if (code === 'HALF_DAY_LEAVE') return 'leave';
  if (code === 'LATE') return 'present';

  if (
    code === 'PRESENT' ||
    code === 'CHECKED_IN' ||
    code === 'CHECKED_OUT' ||
    label.includes('present') ||
    label.includes('checked in') ||
    label.includes('day completed')
  ) {
    return 'present';
  }
  if (code === 'ABSENT' || code === 'LOP' || label === 'lop' || label.includes('absent')) {
    return 'absent';
  }
  if (
    code === 'ON_LEAVE' ||
    code === 'LEAVE' ||
    code === 'HALF_DAY_LEAVE' ||
    (label.includes('leave') && !label.includes('absent'))
  ) {
    return 'leave';
  }
  if (code === 'WFH' || label.includes('wfh') || label.includes('working from home')) {
    return 'wfh';
  }
  if (code === 'HOLIDAY' || label.includes('holiday')) return 'holiday';
  if (
    code === 'PERMISSION' ||
    code === 'TIME_PERMISSION' ||
    label.includes('permission')
  ) {
    return 'permission';
  }
  return 'neutral';
}

/** Uses existing palette only — does not introduce new brand colors. */
export function attendanceToneColor(
  tone: AttendanceCalendarTone,
  palette: ColorPalette,
): string {
  switch (tone) {
    case 'present':
      return palette.success;
    case 'absent':
      return palette.error;
    case 'leave':
      return palette.warning;
    case 'wfh':
      return palette.info;
    case 'holiday':
      return palette.textMuted;
    case 'permission':
      return palette.info;
    default:
      return palette.border;
  }
}

export function monthDateRange(
  year: number,
  monthIndex: number,
): { fromDate: string; toDate: string } {
  const fromDate = toDateOnly(new Date(year, monthIndex, 1));
  const toDate = toDateOnly(new Date(year, monthIndex + 1, 0));
  return { fromDate, toDate };
}

export function parseYearMonth(fromDate: string): { year: number; monthIndex: number } {
  const [yearText, monthText] = fromDate.split('-');
  const year = Number(yearText);
  const monthIndex = Number(monthText) - 1;
  if (
    !Number.isInteger(year) ||
    !Number.isInteger(monthIndex) ||
    monthIndex < 0 ||
    monthIndex > 11
  ) {
    const now = new Date();
    return { year: now.getFullYear(), monthIndex: now.getMonth() };
  }
  return { year, monthIndex };
}

export function buildAttendanceCalendarDays(
  year: number,
  monthIndex: number,
  items: AttendanceHistoryItem[],
  holidays: HolidayRecord[] = [],
): AttendanceCalendarDay[] {
  const byDate = new Map(
    items.map(item => [item.attendanceDate.slice(0, 10), item] as const),
  );
  const holidayMap = holidaysByDate(holidays);
  const first = new Date(year, monthIndex, 1);
  const startOffset = first.getDay();
  const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();
  const totalCells = Math.ceil((startOffset + daysInMonth) / 7) * 7;
  const days: AttendanceCalendarDay[] = [];

  for (let index = 0; index < totalCells; index += 1) {
    const dayNumber = index - startOffset + 1;
    const inMonth = dayNumber >= 1 && dayNumber <= daysInMonth;
    if (!inMonth) {
      days.push({
        date: '',
        day: 0,
        inMonth: false,
        item: null,
        tone: 'neutral',
        holidayName: null,
      });
      continue;
    }
    const date = toDateOnly(new Date(year, monthIndex, dayNumber));
    const item = byDate.get(date) ?? null;
    const holiday = holidayMap.get(date) ?? null;
    const tone = item
      ? resolveAttendanceCalendarTone(item)
      : holiday
        ? 'holiday'
        : 'neutral';
    days.push({
      date,
      day: dayNumber,
      inMonth: true,
      item,
      tone,
      holidayName: holiday?.holidayName ?? null,
    });
  }

  return days;
}

function toDateOnly(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}
